"use client"

import { Check } from "lucide-react"

import { cn } from "@/common/lib/utils"
import RadarChart from "@/modules/lernpfad/components/charts/radar-chart"
import { FRAMEWORK_AXES, type LearningFramework } from "@/modules/lernpfad/data/frameworks"
import type { Localizer } from "@/modules/lernpfad/i18n/localize"

/**
 * One framework as a ledger row.
 *
 * The panel used to render six cards, each repeating the claim, the four or five
 * "was sich ändert" sentences and a source link — 3,000 px of 11 px prose, and
 * six equally-weighted solid-blue "Auswählen" buttons competing for the same
 * click. Content that has to be read once per *decision* was being scaled by the
 * number of options.
 *
 * A row carries only what you compare across frameworks — name, the metric it
 * moves, how much policy it rewrites, and the shape of its emphasis. The prose
 * lives once, in the dossier beside it.
 *
 * The row IS the control: no nested button, so there is exactly one hit target
 * and the panel has no primary-action competition left.
 */

export interface FrameworkRowProps {
    framework: LearningFramework
    localizer: Localizer
    active: boolean
    /** True while this row is only being previewed in the dossier. */
    peeked: boolean
    /** Set for ~2.5 s on the ladder framework a new selection just evicted. */
    replacedBy?: string
    /** Settings this framework writes, vs the house standard. */
    writes: number
    /** Max writes across all frameworks — the meter's full scale. */
    writesScale: number
    onSelect: () => void
    onPeek: () => void
    t: (key: never, values?: Record<string, string | number>) => string
}

export default function FrameworkRow({
    framework,
    localizer,
    active,
    peeked,
    replacedBy,
    writes,
    writesScale,
    onSelect,
    onPeek,
    t,
}: FrameworkRowProps) {
    const content = localizer.framework(framework)
    // Ladders are mutually exclusive, so they are radios; everything else stacks
    // and is a checkbox. Two shapes teach the rule before any label is read, and
    // a radio replacing a radio does not look like a bug.
    const exclusive = framework.family === "ladder"

    return (
        <button
            type="button"
            role={exclusive ? "radio" : "checkbox"}
            aria-checked={active}
            onClick={onSelect}
            onPointerEnter={onPeek}
            onFocus={onPeek}
            className={cn(
                // Grid, not flex: German runs long ("Modalitätsvielfalt",
                // "Entscheidungstest") and must wrap inside its own cell rather
                // than shove the meter or the glyph off the row.
                "grid w-full grid-cols-[1rem_minmax(0,1fr)_2.75rem] items-start gap-x-3 gap-y-1.5",
                "rounded-lg border p-3 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                active
                    ? "border-primary/60 bg-primary/[0.04]"
                    : peeked
                      ? "border-primary/30 bg-primary/[0.02]"
                      : "bg-card hover:border-primary/40 hover:bg-primary/[0.02]",
                replacedBy && "motion-safe:animate-pulse-subtle",
            )}
        >
            <span
                aria-hidden
                className={cn(
                    "mt-0.5 flex size-4 items-center justify-center border-2 transition-colors",
                    exclusive ? "rounded-full" : "rounded-[4px]",
                    active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
                )}
            >
                {active && <Check className="size-2.5" strokeWidth={4} />}
            </span>

            <span className="min-w-0">
                <span className="block text-sm font-semibold leading-tight">{content.name}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                    {framework.author} · {framework.year}
                </span>

                {/* The metric this framework moves. It used to appear only in a
                    separate list AFTER selection — the one field that decides
                    the click was absent at the moment of clicking. */}
                <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                        {content.watch}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span aria-hidden className="flex gap-0.5">
                            {Array.from({ length: writesScale }, (_, i) => (
                                <span
                                    key={i}
                                    className={cn("h-1 w-2.5 rounded-full", i < writes ? "bg-primary" : "bg-muted")}
                                />
                            ))}
                        </span>
                        {t((writes === 1 ? "steuer.writes.one" : "steuer.writes.other") as never, { count: writes })}
                    </span>
                </span>

                {replacedBy && (
                    <span className="mt-1.5 block text-xs font-medium" style={{ color: "var(--lp-warning)" }}>
                        {t("steuer.swap.replacedBy" as never, { name: replacedBy })}
                    </span>
                )}
            </span>

            {/* Shape at a glance. This finally uses `framework.profile`, which
                existed but was only ever read in the combined radar. */}
            <span aria-hidden className={cn("mt-0.5 transition-opacity", !active && "opacity-45")}>
                <RadarChart
                    labels={false}
                    size={44}
                    axes={FRAMEWORK_AXES.map((axis) => ({
                        id: axis,
                        label: axis,
                        value: framework.profile[axis],
                    }))}
                />
            </span>
        </button>
    )
}
